import React, { useState, useEffect } from 'react';
import DocumentViewer from './DocumentViewer';

const DocumentSharing = ({ client, channelName, userType, isOpen, onClose, onDocumentOpen }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Écouter les documents reçus (côté élève)
  useEffect(() => {
    const handleDocumentMessage = (event) => {
      const message = event.detail;
      if (message.type === 'document' || message.type === 'auto_display_document') {
        const doc = message.type === 'auto_display_document' ? message.document : message;
        setCurrentDocument(doc);
        if (onDocumentOpen) onDocumentOpen(true);
      }
    };
    window.addEventListener('agora-document-message', handleDocumentMessage);
    return () => window.removeEventListener('agora-document-message', handleDocumentMessage);
  }, []);

  const uploadAndShare = async (file) => {
    const allowedTypes = ['application/pdf','image/jpeg','image/png','image/gif','image/webp','audio/mpeg','audio/wav','audio/ogg','text/plain'];
    if (!allowedTypes.includes(file.type)) {
      alert('Formats acceptés : PDF, Images, Audio, Texte');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert('Fichier trop volumineux. Maximum 20MB.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('channelName', channelName);
      formData.append('userType', userType);

      const response = await fetch(`${API_BASE_URL}/api/agora/upload-document`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Erreur upload');
      const result = await response.json();

      const doc = {
        type: 'document',
        id: Date.now(),
        fileName: file.name,
        fileUrl: result.fileUrl,
        fileType: file.type,
        fileSize: file.size,
        sharedBy: userType,
        timestamp: new Date().toLocaleTimeString(),
        canPreview: true,
        isInteractive: file.type.includes('pdf') || file.type.includes('image'),
      };

      // Envoyer aux élèves via Agora
      const encoder = new TextEncoder();
      await client.sendStreamMessage(encoder.encode(JSON.stringify(doc)));

      // Afficher aussi côté prof
      setCurrentDocument(doc);

    } catch (error) {
      alert('Erreur lors du partage : ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) uploadAndShare(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadAndShare(file);
  };

  // Si un document est affiché, montrer le viewer (tous les utilisateurs)
  if (currentDocument) {
    return (
      <DocumentViewer
        document={currentDocument}
        userType={userType}
        client={client}
        onClose={() => { setCurrentDocument(null); if (onDocumentOpen) onDocumentOpen(false); }}
      />
    );
  }

  // Panneau share — visible seulement si isOpen ET prof
  if (!isOpen || userType !== 'prof') return null;

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 74, width: 320,
      background: 'rgba(12,12,28,0.97)',
      backdropFilter: 'blur(20px)',
      borderLeft: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', flexDirection: 'column',
      zIndex: 900, padding: 24,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 16, fontWeight: 600 }}>Partager un document</h3>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
            Visible instantanément par l'élève
          </p>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
          fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1
        }}>✕</button>
      </div>

      {/* Dropzone */}
      <label
        htmlFor="doc-upload"
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        style={{
          flex: 1,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 16,
          border: `2px dashed ${isDragging ? 'rgba(99,102,241,0.8)' : 'rgba(255,255,255,0.12)'}`,
          borderRadius: 16,
          background: isDragging ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.03)',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          padding: 24,
        }}
      >
        {isUploading ? (
          <>
            <div style={{ fontSize: 40 }}>⏳</div>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: 14, textAlign: 'center' }}>
              Envoi en cours...
            </p>
          </>
        ) : (
          <>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28,
              boxShadow: '0 8px 24px rgba(99,102,241,0.3)'
            }}>📎</div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#fff', margin: '0 0 6px', fontSize: 15, fontWeight: 600 }}>
                Déposez votre document ici
              </p>
              <p style={{ color: 'rgba(255,255,255,0.35)', margin: 0, fontSize: 12 }}>
                ou cliquez pour sélectionner
              </p>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.2)', margin: 0, fontSize: 11, textAlign: 'center' }}>
              PDF · Images · Audio · Texte — max 20MB
            </p>
          </>
        )}
        <input
          id="doc-upload"
          type="file"
          onChange={handleFileChange}
          disabled={isUploading}
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp3,.wav,.ogg,.txt"
          style={{ display: 'none' }}
        />
      </label>
    </div>
  );
};

export default DocumentSharing;
